import { AloftPrice, isDefined, isUndefined } from '@aloft/toolkit-shared';
import { createSelector } from '@reduxjs/toolkit';

import {
  selectADUCost,
  selectADUCosts,
  selectZipOnlyADUCost,
} from './costSelectors/selectADUCost.ts';
import {
  selectAtticCost,
  selectAtticCosts,
  selectZipOnlyAtticCost,
} from './costSelectors/selectAtticCost.ts';
import {
  selectBarnCost,
  selectBarnCosts,
  selectZipOnlyBarnCost,
} from './costSelectors/selectBarnCost.ts';
import {
  selectBelowGradeFullBathCost,
  selectBelowGradeFullBathCosts,
} from './costSelectors/selectBelowGradeFullBathCost.ts';
import {
  selectBelowGradeHalfBathCost,
  selectBelowGradeHalfBathCosts,
} from './costSelectors/selectBelowGradeHalfBathCost.ts';
import { selectCarportCost, selectCarportCosts } from './costSelectors/selectCarportCost.ts';
import {
  selectDeckCost,
  selectDeckCosts,
  selectZipOnlyDeckCost,
} from './costSelectors/selectDeckCost.ts';
import {
  selectFinishedBasementAreaCost,
  selectFinishedBasementAreaCosts,
} from './costSelectors/selectFinishedBasementAreaCost.ts';
import {
  selectFireplaceCost,
  selectFireplaceCosts,
  selectZipOnlyFireplaceCost,
} from './costSelectors/selectFireplaceCost.ts';
import { selectZipOnlyFoundationCost } from './costSelectors/selectFoundationCost.ts';
import { selectFullBathCost, selectFullBathCosts } from './costSelectors/selectFullBathCost.ts';
import { selectGarageCost, selectGarageCosts } from './costSelectors/selectGarageCost.ts';
import { selectHalfBathCost, selectHalfBathCosts } from './costSelectors/selectHalfBathCost.ts';
import {
  selectMainStructureCost,
  selectMainStructureCosts,
} from './costSelectors/selectMainStructureCost.ts';
import { selectManualCost, selectManualCosts } from './costSelectors/selectManualCost.ts';
import {
  selectPatioCost,
  selectPatioCosts,
  selectZipOnlyPatioCost,
} from './costSelectors/selectPatioCost.ts';
import {
  selectPoolCost,
  selectPoolCosts,
  selectZipOnlyPoolCost,
} from './costSelectors/selectPoolCost.ts';
import {
  selectPorchCost,
  selectPorchCosts,
  selectZipOnlyPorchCost,
} from './costSelectors/selectPorchCost.ts';
import {
  selectShedCost,
  selectShedCosts,
  selectZipOnlyShedCost,
} from './costSelectors/selectShedCost.ts';
import {
  selectSpaCost,
  selectSpaCosts,
  selectZipOnlySpaCost,
} from './costSelectors/selectSpaCost.ts';
import {
  selectTotalBasementAreaCost,
  selectTotalBasementAreaCosts,
} from './costSelectors/selectTotalBasementAreaCost.ts';
import {
  selectWorkshopCost,
  selectWorkshopCosts,
  selectZipOnlyWorkshopCost,
} from './costSelectors/selectWorkshopCost.ts';
import { selectCompSequence } from './orderSelectors.ts';

const calculateCostNew = (
  mainStructureCost: ReturnType<typeof selectMainStructureCost> | undefined,
  garage: ReturnType<typeof selectGarageCost> | undefined,
  carport: ReturnType<typeof selectCarportCost> | undefined,
  basementFinished: ReturnType<typeof selectFinishedBasementAreaCost> | undefined,
  basementTotal: ReturnType<typeof selectTotalBasementAreaCost> | undefined,
  attic: ReturnType<typeof selectAtticCost> | undefined,
  fireplace: ReturnType<typeof selectFireplaceCost> | undefined,
  fullBaths: ReturnType<typeof selectFullBathCost> | undefined,
  halfBaths: ReturnType<typeof selectHalfBathCost> | undefined,
  belowGradeFullBaths: ReturnType<typeof selectBelowGradeFullBathCost> | undefined,
  belowGradeHalfBaths: ReturnType<typeof selectBelowGradeHalfBathCost> | undefined,
  shed: ReturnType<typeof selectShedCost> | undefined,
  workshop: ReturnType<typeof selectWorkshopCost> | undefined,
  barn: ReturnType<typeof selectBarnCost> | undefined,
  pool: ReturnType<typeof selectPoolCost> | undefined,
  spa: ReturnType<typeof selectSpaCost> | undefined,
  adu: ReturnType<typeof selectADUCost> | undefined,
  porch: ReturnType<typeof selectPorchCost> | undefined,
  patio: ReturnType<typeof selectPatioCost> | undefined,
  deck: ReturnType<typeof selectDeckCost> | undefined,
  manualCost: ReturnType<typeof selectManualCost> | undefined,
) => {
  if (isUndefined(mainStructureCost)) {
    return undefined;
  }

  return [
    mainStructureCost,
    garage,
    carport,
    basementFinished,
    basementTotal,
    attic,
    fireplace,
    fullBaths,
    halfBaths,
    belowGradeFullBaths,
    belowGradeHalfBaths,
    shed,
    workshop,
    barn,
    pool,
    spa,
    adu,
    porch,
    patio,
    deck,
    manualCost,
  ].reduce((acc: AloftPrice, curr) => acc.add(curr), AloftPrice.zero);
};

const calculateAdditionalStructuresCostNew = (
  attic: ReturnType<typeof selectAtticCost> | undefined,
  fireplace: ReturnType<typeof selectFireplaceCost> | undefined,
  shed: ReturnType<typeof selectShedCost> | undefined,
  workshop: ReturnType<typeof selectWorkshopCost> | undefined,
  barn: ReturnType<typeof selectBarnCost> | undefined,
  pool: ReturnType<typeof selectPoolCost> | undefined,
  spa: ReturnType<typeof selectSpaCost> | undefined,
  adu: ReturnType<typeof selectADUCost> | undefined,
  porch: ReturnType<typeof selectPorchCost> | undefined,
  patio: ReturnType<typeof selectPatioCost> | undefined,
  deck: ReturnType<typeof selectDeckCost> | undefined,
  manualCost: ReturnType<typeof selectManualCost> | undefined,
) =>
  AloftPrice.addWithNulls(
    attic,
    fireplace,
    shed,
    workshop,
    barn,
    pool,
    spa,
    adu,
    porch,
    patio,
    deck,
    manualCost,
  );

export const selectCostNew = createSelector(
  [
    selectMainStructureCost,
    selectGarageCost,
    selectCarportCost,
    selectFinishedBasementAreaCost,
    selectTotalBasementAreaCost,
    selectAtticCost,
    selectFireplaceCost,
    selectFullBathCost,
    selectHalfBathCost,
    selectBelowGradeFullBathCost,
    selectBelowGradeHalfBathCost,
    selectShedCost,
    selectWorkshopCost,
    selectBarnCost,
    selectPoolCost,
    selectSpaCost,
    selectADUCost,
    selectPorchCost,
    selectPatioCost,
    selectDeckCost,
    selectManualCost,
  ],
  calculateCostNew,
);

export const selectAdditionalStructureCostNew = createSelector(
  [
    selectAtticCost,
    selectFireplaceCost,
    selectShedCost,
    selectWorkshopCost,
    selectBarnCost,
    selectPoolCost,
    selectSpaCost,
    selectADUCost,
    selectPorchCost,
    selectPatioCost,
    selectDeckCost,
    selectManualCost,
  ],
  calculateAdditionalStructuresCostNew,
);

export const selectZipOnlyAdditionalStructureCostNew = createSelector(
  [
    selectZipOnlyAtticCost,
    selectZipOnlyFireplaceCost,
    selectZipOnlyShedCost,
    selectZipOnlyWorkshopCost,
    selectZipOnlyBarnCost,
    selectZipOnlyPoolCost,
    selectZipOnlySpaCost,
    selectZipOnlyADUCost,
    selectZipOnlyPorchCost,
    selectZipOnlyPatioCost,
    selectZipOnlyDeckCost,
    selectManualCost,
    selectZipOnlyFoundationCost,
  ],
  calculateAdditionalStructuresCostNew,
);

export const selectCostNews = createSelector(
  [
    selectMainStructureCosts,
    selectGarageCosts,
    selectCarportCosts,
    selectFinishedBasementAreaCosts,
    selectTotalBasementAreaCosts,
    selectAtticCosts,
    selectFireplaceCosts,
    selectFullBathCosts,
    selectHalfBathCosts,
    selectBelowGradeFullBathCosts,
    selectBelowGradeHalfBathCosts,
    selectShedCosts,
    selectWorkshopCosts,
    selectBarnCosts,
    selectPoolCosts,
    selectSpaCosts,
    selectADUCosts,
    selectPorchCosts,
    selectPatioCosts,
    selectDeckCosts,
    selectManualCosts,
    selectCompSequence,
  ],
  (
    mainStructureCosts,
    garages,
    carports,
    basementFinisheds,
    basementTotals,
    attics,
    fireplaces,
    fullBaths,
    halfBaths,
    belowGradeFullBaths,
    belowGradeHalfBaths,
    sheds,
    workshops,
    barns,
    pools,
    spas,
    adus,
    porches,
    patios,
    decks,
    manualCosts,
    compSequence,
  ) => {
    const properties = ['subject', ...compSequence];

    return properties.reduce((acc: Record<string, AloftPrice>, propID) => {
      const cost = calculateCostNew(
        mainStructureCosts[propID],
        garages[propID],
        carports[propID],
        basementFinisheds[propID],
        basementTotals[propID],
        attics[propID],
        fireplaces[propID],
        fullBaths[propID],
        halfBaths[propID],
        belowGradeFullBaths[propID],
        belowGradeHalfBaths[propID],
        sheds[propID],
        workshops[propID],
        barns[propID],
        pools[propID],
        spas[propID],
        adus[propID],
        porches[propID],
        patios[propID],
        decks[propID],
        manualCosts[propID],
      );

      if (isDefined(cost)) acc[propID] = cost;

      return acc;
    }, {});
  },
);

export const selectCostNewDisplay = createSelector([selectCostNew], (costNew) => {
  if (isUndefined(costNew)) {
    return undefined;
  }

  return costNew.toString(true);
});
